/**
 * The quarter-by-quarter line score.
 *
 * A final score says who won; a line score says how. "Trailed by ten at half"
 * and "scored 21 unanswered in the third" are the sentences league write-ups
 * are made of, and neither is recoverable from 31-24 — so the periods are
 * recorded alongside the total rather than derived from it.
 *
 * Both sides are stored as one array each, index 0-3 being Q1-Q4 and index 4
 * the overtime period. Keeping overtime in the same array as regulation means
 * the invariant that matters — the periods add up to the final score — is one
 * sum either way.
 *
 * The league plays NFL rules: one overtime, and a game that is still level at
 * the end of it is a tie. So a line score can be level all the way down, and
 * five periods is as long as a game gets.
 */
/** Quarters in a full game before anyone goes to overtime. */
export const REGULATION_PERIODS = 4;
/** Q1-Q4 plus the one overtime NFL rules allow. Nothing longer exists. */
export const MAX_PERIODS = REGULATION_PERIODS + 1;
/** "Q1"…"Q4", then "OT" — how a box score heading reads. */
export function periodLabel(index) {
    return index < REGULATION_PERIODS ? `Q${index + 1}` : "OT";
}
export function sumPeriods(periods) {
    return periods.reduce((total, p) => total + p, 0);
}
/** Level after `count` periods — which is the only reason to play another one. */
export function tiedAfter(line, count) {
    return (sumPeriods(line.home.slice(0, count)) === sumPeriods(line.away.slice(0, count)));
}
/** True when the line score records any overtime at all. */
export function hasOvertime(line) {
    return line.home.length > REGULATION_PERIODS;
}
/**
 * Why this line score can't be right, in a sentence, or null when it is fine.
 *
 * Shared by the entry form and the server so a manager sees the problem next to
 * the boxes rather than as a rejection after submitting, and so the two can
 * never disagree about what counts as valid.
 *
 * `homeScore`/`awayScore` are the final score as the same person reported it:
 * a line score that doesn't add up to the score beside it is the mis-entry
 * this is really guarding against.
 */
export function lineScoreProblem(line, homeScore, awayScore) {
    if (line.home.length !== line.away.length) {
        return "Both teams need the same number of periods.";
    }
    if (line.home.length < REGULATION_PERIODS) {
        return "Every quarter needs a number — put 0 where nobody scored.";
    }
    if (line.home.length > MAX_PERIODS) {
        return "A game is four quarters and at most one overtime.";
    }
    for (const [i, value] of [...line.home, ...line.away].entries()) {
        if (!Number.isInteger(value) || value < 0) {
            return `${periodLabel(i % line.home.length)} has to be a whole number, 0 or more.`;
        }
    }
    // An overtime period exists because regulation finished level. Anything else
    // means a quarter has been typed in the wrong column, which is the mistake
    // worth catching here. Nothing checks the other direction: a game level after
    // four quarters may well have gone to overtime and finished level anyway, and
    // a manager who only wrote down the four quarters of a tie is not wrong.
    for (let count = REGULATION_PERIODS; count < line.home.length; count++) {
        if (!tiedAfter(line, count)) {
            return `There's no ${periodLabel(count)} to fill in — the game wasn't level after ${periodLabel(count - 1)}.`;
        }
    }
    if (homeScore != null && sumPeriods(line.home) !== homeScore) {
        return `The home quarters add up to ${sumPeriods(line.home)}, not ${homeScore}.`;
    }
    if (awayScore != null && sumPeriods(line.away) !== awayScore) {
        return `The away quarters add up to ${sumPeriods(line.away)}, not ${awayScore}.`;
    }
    return null;
}
/**
 * Read a line score off a stored value, or null if there isn't a usable one.
 *
 * The column is jsonb, so anything could be in it — including rows written
 * before the column existed, which hold null.
 */
export function asLineScore(value) {
    if (!value || typeof value !== "object")
        return null;
    const { home, away } = value;
    if (!Array.isArray(home) || !Array.isArray(away))
        return null;
    if (home.length === 0 || home.length !== away.length)
        return null;
    if (![...home, ...away].every((v) => typeof v === "number" && Number.isFinite(v))) {
        return null;
    }
    return { home: home, away: away };
}
/**
 * Why one side's quarters can't be right, or null when they are fine.
 *
 * The half-report equivalent of `lineScoreProblem`: a manager filing their own
 * half has quarters for one side only, so the cross-side checks (equal length,
 * an overtime that regulation actually earned) can't run until the two halves
 * are put together.
 */
export function halfLineProblem(periods, score) {
    if (periods.length < REGULATION_PERIODS) {
        return "Every quarter needs a number — put 0 where nobody scored.";
    }
    if (periods.length > MAX_PERIODS) {
        return "A game is four quarters and at most one overtime.";
    }
    for (const [i, value] of periods.entries()) {
        if (!Number.isInteger(value) || value < 0) {
            return `${periodLabel(i)} has to be a whole number, 0 or more.`;
        }
    }
    if (score != null && sumPeriods(periods) !== score) {
        return `Those quarters add up to ${sumPeriods(periods)}, not ${score}.`;
    }
    return null;
}
/**
 * Read a possibly one-sided line score off a stored value.
 *
 * Returns both sides as null when there is nothing usable, so a caller can
 * always destructure it.
 */
export function asPartialLineScore(value) {
    const empty = { home: null, away: null };
    if (!value || typeof value !== "object")
        return empty;
    const { home, away } = value;
    const side = (v) => Array.isArray(v) &&
        v.length > 0 &&
        v.every((n) => typeof n === "number" && Number.isFinite(n))
        ? v
        : null;
    return { home: side(home), away: side(away) };
}
/**
 * Put two halves together into the game's line score, or null when they don't
 * make one.
 *
 * The halves come from different reports, so they can disagree about how many
 * periods there were: a manager who was shut out in overtime may well have
 * written down four quarters, not five. Padding the shorter side with zeroes
 * only holds if its quarters still add up to its score, which is what the
 * validation below is for — anything that fails it is dropped rather than
 * shown, because a breakdown that doesn't match the scoreline beside it is
 * worse than no breakdown at all.
 */
export function mergeLineScores(half, homeScore, awayScore) {
    if (!half.home || !half.away)
        return null;
    const periods = Math.max(half.home.length, half.away.length);
    const pad = (side) => [
        ...side,
        ...Array.from({ length: periods - side.length }, () => 0),
    ];
    const merged = { home: pad(half.home), away: pad(half.away) };
    return lineScoreProblem(merged, homeScore, awayScore) ? null : merged;
}
//# sourceMappingURL=lineScore.js.map